import { useState, useEffect, Fragment } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import FleetWebApi from "../Api/FleetWebApi";
import SignalCellular0BarIcon from "@mui/icons-material/SignalCellular0Bar";
import SignalCellular1BarIcon from "@mui/icons-material/SignalCellular1Bar";
import SignalCellular2BarIcon from "@mui/icons-material/SignalCellular2Bar";
import SignalCellular3BarIcon from "@mui/icons-material/SignalCellular3Bar";
import SignalCellular4BarIcon from "@mui/icons-material/SignalCellular4Bar";

function RenderArrayItems(arr) {
  return Object.keys(arr).map((idx) => <li key={idx}>{arr[idx]}</li>);
}

function scoreColor(score) {
  if (score === "Good") return "#4CAF50";
  if (score === "Okay") return "#FFC107";
  if (score === "Bad") return "#FF7043";
  if (score === "Very Bad") return "#F44336";
  return "inherit";
}

function CellBarsIcon({ value }) {
  const bars = Math.round(value);
  const color = bars <= 1 ? "error" : bars <= 2 ? "warning" : "success";
  if (bars === 0) return <SignalCellular0BarIcon color={color} fontSize="small" />;
  if (bars === 1) return <SignalCellular1BarIcon color={color} fontSize="small" />;
  if (bars === 2) return <SignalCellular2BarIcon color={color} fontSize="small" />;
  if (bars === 3) return <SignalCellular3BarIcon color={color} fontSize="small" />;
  return <SignalCellular4BarIcon color={color} fontSize="small" />;
}

function formatUptime(seconds) {
  if (!seconds) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function Metric({ label, value, score, children }) {
  return value != null ? (
    <li style={{ marginBottom: 4, color: score ? scoreColor(score) : "inherit" }}>
      {label}:{" "}
      {children ?? value}
    </li>
  ) : null;
}

function CellConnection(props) {
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState();
  const [connectionData, setConnectionData] = useState();

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setConnectionData(null);
    setError(null);

    FleetWebApi.getCellConnectionSummary(
      props.coreId,
      props.startDate,
      props.endDate
    )
      .then((resp) => {
        if (isMounted) {
          setConnectionData(resp);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message);
          setIsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [props.coreId, props.startDate, props.endDate]);

  if (isLoading) {
    return <CircularProgress />;
  }

  const d = connectionData;

  return (
    <Container maxWidth="lg" className="container">
      <h3>Connection Summary</h3>
      {error ? (
        <p style={{ color: "#FFC107" }}>No connection data available for selected range.</p>
      ) : !d ? null : (
        <Grid container spacing={3}>
          {/* Col 1 — problems / status */}
          <Grid item xs={12} md={4}>
            <h4>Detected Problems</h4>
            {d.problems && d.problems.length > 0 ? (
              <Fragment>
                <ul style={{ color: "#F44336" }}>
                  {RenderArrayItems(d.problems)}
                </ul>
              </Fragment>
            ) : (
              <ul style={{ color: "#4CAF50" }}>No problems detected</ul>
            )}
          </Grid>

          {/* Col 2 — signal & connectivity */}
          <Grid item xs={12} md={4}>
            <h4>Signal & Connectivity</h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              <Metric
                label="Avg Cell Bars"
                value={d.average_cell_bars?.value}
                score={d.average_cell_bars?.score}
              >
                {d.average_cell_bars?.value != null && (
                  <span style={{ verticalAlign: "middle" }}>
                    {d.average_cell_bars.value.toFixed(1)}{" "}
                    <CellBarsIcon value={d.average_cell_bars.value} />
                  </span>
                )}
              </Metric>

              <Metric
                label="Latest Cell Bars"
                value={d.latest_cell_bars?.value}
                score={d.latest_cell_bars?.score}
              >
                {d.latest_cell_bars?.value != null && (
                  <span style={{ verticalAlign: "middle" }}>
                    {d.latest_cell_bars.value}{" "}
                    <CellBarsIcon value={d.latest_cell_bars.value} />
                  </span>
                )}
              </Metric>

              <Metric
                label="Cloud Online"
                value={d.cellOnline?.value}
                score={d.cellOnline?.score}
              >
                {d.cellOnline?.value != null && `${d.cellOnline.value.toFixed(1)}%`}
              </Metric>

              <Metric
                label="Network Online"
                value={d.networkOnline?.value}
                score={d.networkOnline?.score}
              >
                {d.networkOnline?.value != null && `${d.networkOnline.value.toFixed(1)}%`}
              </Metric>

              <Metric
                label="Outages (p=1)"
                value={d.outages?.value}
                score={d.outages?.score}
              />
            </ul>
          </Grid>

          {/* Col 3 — device info */}
          <Grid item xs={12} md={4}>
            <h4>Device Info</h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {d.operators?.value != null && d.operators.value > 0 && (
                <Metric
                  label="Distinct Operators"
                  value={d.operators.value}
                />
              )}

              <Metric label="Distinct Tower Sites" value={d.towers?.value} />

              {d.uptime?.value != null && (
                <Metric label="Device Uptime" value={d.uptime.value}>
                  {formatUptime(d.uptime.value)}
                </Metric>
              )}
            </ul>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default CellConnection;
